import {
  type UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

// Ceiling on a page's wait: on a busy main thread the idle callback alone may
// never come.
const PAGE_WORK_IDLE_TIMEOUT_MS = 500

// Quiet required after the reader's last input anywhere on the page — not just
// in this viewer. A slot spent mid-gesture freezes whatever that gesture is
// moving, and long enough to bridge the gap between events on a slow wheel.
const PAGE_WORK_INPUT_QUIET_MS = 150

// The quiet required while the reader is somewhere other than the document.
// 150ms gaps are everywhere inside typing or hovering, so builds kept landing
// mid-interaction, and nothing over there is waiting on this document anyway.
const PAGE_WORK_AWAY_INPUT_QUIET_MS = 600

// How long a page waits for a reader who never brings a pointer — keyboard
// navigation, or a screen reader. Past the moment the canvas settles.
const PAGE_WORK_UNATTENDED_MS = 1000

interface PageWorkItem {
  build: () => void
  queuedAt: number
  /** Work for a page nobody can see yet; yields to work for a page they can. */
  prefetch: boolean
}

/**
 * pdf.js builds text and annotation layers synchronously, measuring every span
 * against a canvas context, and each measurement forces the layout it just
 * invalidated — tens of milliseconds a page, and the virtualizer's overscan
 * lands several pages' worth in the frames the canvas appears in. An overscan
 * canvas costs about the same, in the paint pass its raster ends in.
 *
 * So this queue hands out one page of that work per idle slot, and holds every
 * slot until the reader's input goes quiet anywhere on the page: the idle
 * timeout fires between the frames of a gesture, and a slot spent there is the
 * freeze the queue exists to prevent. Canvases for pages actually in the
 * viewport bypass the queue — a blank visible page is worse than a busy frame.
 *
 * Exempting canvas prefetch from the input hold measured worse in both
 * directions: mid-gesture rasters janked the scroll and still lost the race,
 * because a fling reveals pages faster than any raster can land.
 */
const pendingPageWork: PageWorkItem[] = []
let cancelScheduledSlot: (() => void) | null = null
let lastUserInputAt = 0
let readersAtDocument = 0

const holdPageWorkForUserInput = () => {
  lastUserInputAt = performance.now()
}

// Listened for in the capture phase, because scroll does not bubble and the
// scroller that matters may be any pane on the page. Attached only while work
// is queued.
const USER_INPUT_EVENTS = [
  'scroll',
  'wheel',
  'touchmove',
  'pointermove',
  'pointerdown',
  'keydown',
] as const

let isWatchingUserInput = false

const watchUserInput = () => {
  if (isWatchingUserInput) return
  isWatchingUserInput = true
  for (const eventType of USER_INPUT_EVENTS) {
    window.addEventListener(eventType, holdPageWorkForUserInput, {
      capture: true,
      passive: true,
    })
  }
}

const unwatchUserInput = () => {
  if (!isWatchingUserInput) return
  isWatchingUserInput = false
  for (const eventType of USER_INPUT_EVENTS) {
    window.removeEventListener(eventType, holdPageWorkForUserInput, {
      capture: true,
    })
  }
}

const enqueuePageWork = (pageWork: PageWorkItem) => {
  pendingPageWork.push(pageWork)
  watchUserInput()
  schedulePageWorkSlot()
}

const withdrawPageWork = (pageWork: PageWorkItem) => {
  const queued = pendingPageWork.indexOf(pageWork)
  if (queued !== -1) {
    pendingPageWork.splice(queued, 1)
  }
  if (pendingPageWork.length === 0) {
    cancelScheduledSlot?.()
    cancelScheduledSlot = null
    unwatchUserInput()
  }
}

const runNextPageWork = () => {
  cancelScheduledSlot = null
  // Work for a page the reader can see goes before prefetch for one they may
  // never reach, whichever order the two were queued in.
  const firstNeededIndex = pendingPageWork.findIndex((work) => !work.prefetch)
  const nextIndex = firstNeededIndex === -1 ? 0 : firstNeededIndex
  const nextPageWork = pendingPageWork[nextIndex]
  if (!nextPageWork) {
    unwatchUserInput()
    return
  }

  const now = performance.now()
  const quietForMs = now - lastUserInputAt
  const requiredQuietMs =
    readersAtDocument > 0
      ? PAGE_WORK_INPUT_QUIET_MS
      : PAGE_WORK_AWAY_INPUT_QUIET_MS
  if (quietForMs < requiredQuietMs) {
    schedulePageWorkSlotIn(requiredQuietMs - quietForMs)
    return
  }

  const unattendedFor = now - nextPageWork.queuedAt
  if (readersAtDocument === 0 && unattendedFor < PAGE_WORK_UNATTENDED_MS) {
    schedulePageWorkSlotIn(PAGE_WORK_UNATTENDED_MS - unattendedFor)
    return
  }

  pendingPageWork.splice(nextIndex, 1)
  nextPageWork.build()
  if (pendingPageWork.length > 0) {
    schedulePageWorkSlot()
  } else {
    unwatchUserInput()
  }
}

const schedulePageWorkSlotIn = (delayMs: number) => {
  const handle = window.setTimeout(runNextPageWork, delayMs)
  cancelScheduledSlot = () => window.clearTimeout(handle)
}

const schedulePageWorkSlot = () => {
  if (cancelScheduledSlot) return
  // Safari only picked up requestIdleCallback recently.
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(runNextPageWork, {
      timeout: PAGE_WORK_IDLE_TIMEOUT_MS,
    })
    cancelScheduledSlot = () => window.cancelIdleCallback(handle)
    return
  }
  schedulePageWorkSlotIn(PAGE_WORK_IDLE_TIMEOUT_MS)
}

/**
 * Pointer handlers telling the queue whether anyone is at the document. Spread
 * them on the scroll container, not on individual pages.
 *
 * Counted rather than flagged because leaving one viewer and entering another
 * is two events with no guaranteed order, and released on unmount because a
 * viewer that closes under the pointer never gets to say the reader left.
 */
export const useReaderPresence = () => {
  const isReaderPresentRef = useRef(false)

  const setReaderPresent = useCallback((isPresent: boolean) => {
    if (isReaderPresentRef.current === isPresent) return
    isReaderPresentRef.current = isPresent
    readersAtDocument = Math.max(0, readersAtDocument + (isPresent ? 1 : -1))

    // The queue is sitting on a timer it no longer needs to wait out.
    if (isPresent && pendingPageWork.length > 0) {
      cancelScheduledSlot?.()
      cancelScheduledSlot = null
      schedulePageWorkSlot()
    }
  }, [])

  useEffect(() => () => setReaderPresent(false), [setReaderPresent])

  return {
    onPointerEnter: useCallback(
      () => setReaderPresent(true),
      [setReaderPresent],
    ),
    onPointerLeave: useCallback(
      () => setReaderPresent(false),
      [setReaderPresent],
    ),
  }
}

/**
 * Takes whether the page is painted and on screen, and returns false until its
 * turn in the idle queue comes up. Feed the result to react-pdf's
 * `renderTextLayer`. Not to `renderAnnotationLayer` — that one holds the links
 * and form fields, and a link that is not there yet is a click that lands on
 * nothing.
 *
 * Both layers are absolutely positioned, and citation and search highlights
 * are placed from the page's text content rather than the layer's DOM, so
 * arriving late moves nothing on screen.
 *
 * `buildKey` is the scale the page renders at. react-pdf rebuilds the layers
 * whenever that prop changes, and letting every mounted page do so in the
 * settle commit is a multi-second freeze on a zoom step; keying the grant
 * revokes it in that commit and each page rebuilds one idle slot at a time.
 */
export const useIdlePageLayers = (
  isPageReady: boolean,
  buildKey: number,
): boolean => {
  const [builtKey, setBuiltKey] = useState<number | null>(null)
  const areLayersBuilt = builtKey === buildKey

  useEffect(() => {
    if (!isPageReady) return
    // A page scrolled away and back keeps its layers; re-queueing would put a
    // page that has nothing behind a page that needs nothing.
    if (areLayersBuilt) return

    const pageWork = {
      build: () => setBuiltKey(buildKey),
      queuedAt: performance.now(),
      prefetch: false,
    }
    enqueuePageWork(pageWork)
    return () => withdrawPageWork(pageWork)
  }, [isPageReady, areLayersBuilt, buildKey])

  return areLayersBuilt
}

/**
 * Takes whether the page is in the viewport and returns whether its canvas may
 * render. Feed the result to react-pdf's `renderMode` — `'canvas'` when
 * granted, `'none'` while waiting.
 *
 * The worker only parses the page; the pixels go down here, on the main
 * thread, in a raster nothing can interrupt. Every mounted page used to start
 * one at once, so a citation jump put five or six of them in contention with
 * the single page the reader was looking at.
 *
 * A page in the viewport is granted immediately and never queues; overscan
 * pages take prefetch slots, behind every visible page's layers.
 *
 * `buildKey` is the scale the page renders at, and revokes the grant on a
 * scale change — but only off screen, where stale pixels are nobody's problem.
 * Revoking a visible page's grant would flash its canvas away mid-zoom, and
 * react-pdf repaints that one in place.
 */
export const useIdleOverscanCanvas = (
  isOnScreen: boolean,
  buildKey: number,
): boolean => {
  const [grantedKey, setGrantedKey] = useState<number | null>(() =>
    isOnScreen ? buildKey : null,
  )
  // Adjusted during render, not in an effect: a page scrolling into view before
  // its prefetch slot came up would otherwise spend the commit at renderMode
  // 'none', which is a blank frame where the page should be.
  if (isOnScreen && grantedKey !== buildKey) {
    setGrantedKey(buildKey)
  }
  const isCanvasGranted = isOnScreen || grantedKey === buildKey

  useEffect(() => {
    if (isOnScreen || grantedKey === buildKey) return

    const pageWork = {
      build: () => setGrantedKey(buildKey),
      queuedAt: performance.now(),
      prefetch: true,
    }
    enqueuePageWork(pageWork)
    return () => withdrawPageWork(pageWork)
  }, [isOnScreen, grantedKey, buildKey])

  return isCanvasGranted
}

// Frames rather than milliseconds: a gesture produces one change per frame
// however long the frames are, so a wall-clock window would read a dropped
// frame as the end of the drag. Two, so alternate-frame changes still count.
const PAGE_SCALE_QUIET_FRAMES = 2

/**
 * Returns the scale to render at — the one asked for, once it stops moving —
 * and whether it is moving right now.
 *
 * A fit-width viewer recomputes its scale from its container on every frame of
 * a pane drag, and pdf.js re-rasterizes every mounted page each time, text
 * layer included. Waiting for the width to settle makes that one render for
 * the whole drag. Everything downstream reads the returned scale — page boxes,
 * virtualizer estimates, citation overlays — so nothing drifts out of register
 * with what was drawn.
 *
 * `isSettling` drops the text layers for the length of the drag: they are
 * mounted at a size they are about to lose, every span counts against the
 * style recalc each pointer move forces, and the rebuild at the end was going
 * to happen regardless.
 */
export const useSettledPageScale = (
  requestedPageScale: number,
): { pageScale: number; isSettling: boolean } => {
  const [settledPageScale, setSettledPageScale] = useState(requestedPageScale)

  useEffect(() => {
    if (requestedPageScale === settledPageScale) return

    let quietFrames = 0
    let frame = requestAnimationFrame(function waitForQuiet() {
      quietFrames += 1
      if (quietFrames >= PAGE_SCALE_QUIET_FRAMES) {
        setSettledPageScale(requestedPageScale)
        return
      }
      frame = requestAnimationFrame(waitForQuiet)
    })

    // Cancelled and restarted by the next requested scale, so a gesture never
    // accumulates the quiet it needs to commit.
    return () => cancelAnimationFrame(frame)
  }, [requestedPageScale, settledPageScale])

  return useMemo(
    () => ({
      pageScale: settledPageScale,
      isSettling: requestedPageScale !== settledPageScale,
    }),
    [requestedPageScale, settledPageScale],
  )
}

interface UsePdfScrollObserverProps {
  getCurrentPage: () => number | undefined
  onPageChange: (pageNumber: number) => void
  onScrollPositionChange?: (scrollTop: number) => void
  isReady?: boolean
}

export const usePdfScrollObserver = ({
  getCurrentPage,
  onPageChange,
  onScrollPositionChange,
  isReady = true,
}: UsePdfScrollObserverProps) => {
  // Use a ref to track the current page to avoid recreating the callback on every page change
  const currentPageRef = useRef<number>(1)
  const scrollFrameRef = useRef<number | null>(null)
  const pendingScrollTopRef = useRef<number | null>(null)
  const onScrollPositionChangeRef = useRef(onScrollPositionChange)
  onScrollPositionChangeRef.current = onScrollPositionChange

  const findMostVisiblePage = useCallback(() => {
    if (!isReady) return
    const currentPage = getCurrentPage()
    if (!currentPage || currentPage === currentPageRef.current) return

    currentPageRef.current = currentPage
    onPageChange(currentPage)
  }, [getCurrentPage, onPageChange, isReady])

  const flushPendingScrollPosition = useCallback(() => {
    const pendingScrollTop = pendingScrollTopRef.current
    pendingScrollTopRef.current = null
    if (pendingScrollTop !== null) {
      onScrollPositionChangeRef.current?.(pendingScrollTop)
    }
  }, [])

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      pendingScrollTopRef.current = event.currentTarget.scrollTop
      // The window watcher exists only while work is queued, and this scroll is
      // what mounts the pages about to queue — so hold before they arrive.
      holdPageWorkForUserInput()
      if (scrollFrameRef.current !== null) return

      scrollFrameRef.current = requestAnimationFrame(() => {
        scrollFrameRef.current = null
        findMostVisiblePage()
        flushPendingScrollPosition()
      })
    },
    [findMostVisiblePage, flushPendingScrollPosition],
  )

  useEffect(() => {
    return () => {
      if (scrollFrameRef.current !== null) {
        cancelAnimationFrame(scrollFrameRef.current)
      }
      flushPendingScrollPosition()
    }
  }, [flushPendingScrollPosition])

  return handleScroll
}
